import { BATTLE_ARENA_FIGHT_ROSTER } from './battleArenaFightRoster'
/**
 * Elyon elemental presentation contract consumed by Rift Fighters.
 *
 * This is a versioned immutable snapshot of Elyon's content authority. Fight
 * owns timing, hit detection and state transitions; Elyon owns the vocabulary,
 * palette, elemental response and skill-to-effect mapping.
 */
import type { AttackKind } from './simulation'

export const ELYON_ELEMENTAL_FX_VERSION = 'elyon-elemental-fx/2026.09.16.v1'

export type ElementalFxId =
  | 'physical'
  | 'arcane'
  | 'fire-ember'
  | 'fire-blue'
  | 'fire-violet'
  | 'water'
  | 'air'
  | 'ice'
  | 'lightning'
  | 'poison'
  | 'acid'
  | 'nature'
  | 'earth'
  | 'void'
  | 'light'

export type StageMaterial = 'stone' | 'crystal' | 'wood' | 'metal' | 'organic' | 'earth' | 'water'

export interface ElementalFxProfile {
  readonly id: ElementalFxId
  readonly element: ElementalFxId | 'fire'
  readonly label: string
  readonly primary: readonly [string, string, string]
  readonly accent: string
  readonly trail: string
  readonly particle: 'spark' | 'flame' | 'droplet' | 'ribbon' | 'shard' | 'cloud' | 'corrosion' | 'leaf' | 'void' | 'glow'
  readonly gravity: number
  readonly drag: number
  readonly status?: 'burn' | 'soak' | 'gust' | 'freeze' | 'shock' | 'poison' | 'corrode' | 'rift'
  readonly destruction: {
    readonly damageType: 'impact' | 'implosive' | 'thermal' | 'water' | 'cold' | 'electric' | 'poison' | 'acid' | 'crush'
    readonly heat?: number
    readonly wetness?: number
    readonly corrosion?: number
    readonly impulseScale?: number
    readonly materialMultipliers: Readonly<Partial<Record<StageMaterial, number>>>
  }
  readonly arena: {
    readonly hazard: 'none' | 'flame-field' | 'steam-field' | 'wind-field' | 'poison-cloud' | 'acid-pool' | 'frost-field' | 'rift-field'
    readonly radius: number
    readonly lifetime: number
    readonly intensity: number
  }
}

const makeProfile = (
  id: ElementalFxId,
  element: ElementalFxProfile['element'],
  label: string,
  primary: readonly [string, string, string],
  accent: string,
  trail: string,
  particle: ElementalFxProfile['particle'],
  destruction: ElementalFxProfile['destruction'],
  arena: ElementalFxProfile['arena'],
  extra: Pick<ElementalFxProfile, 'gravity' | 'drag' | 'status'> = { gravity: 0, drag: 0.6 },
): ElementalFxProfile => ({
  id,
  element,
  label,
  primary,
  accent,
  trail,
  particle,
  gravity: extra.gravity,
  drag: extra.drag,
  ...(extra.status ? { status: extra.status } : {}),
  destruction,
  arena,
})

export const ELYON_ELEMENTAL_FX: Readonly<Record<ElementalFxId, ElementalFxProfile>> = {
  physical: makeProfile(
    'physical', 'physical', 'Impact',
    ['#ffd27a', '#ffaa44', '#ffffff'], '#ffffff', '#ffd27a', 'spark',
    { damageType: 'impact', impulseScale: 1, materialMultipliers: {} },
    { hazard: 'none', radius: 0, lifetime: 0, intensity: 0 },
    { gravity: 8, drag: 0 },
  ),
  arcane: makeProfile(
    'arcane', 'arcane', 'Arcane',
    ['#a78bfa', '#7c4dff', '#e0d4ff'], '#ffffff', '#a78bfa', 'glow',
    { damageType: 'implosive', impulseScale: 1.1, materialMultipliers: { crystal: 1.12 } },
    { hazard: 'rift-field', radius: 2.4, lifetime: 1.1, intensity: 0.7 },
    { gravity: -0.2, drag: 0.3, status: 'rift' },
  ),
  'fire-ember': makeProfile(
    'fire-ember', 'fire', 'Ember Flame',
    ['#ff3b1a', '#ff7a33', '#ffb347'], '#fff2b0', '#ff7a33', 'flame',
    { damageType: 'thermal', heat: 650, impulseScale: 1.05, materialMultipliers: { wood: 1.8, organic: 1.65, crystal: 1.12 } },
    { hazard: 'flame-field', radius: 2.25, lifetime: 1.5, intensity: 0.85 },
    { gravity: -2.5, drag: 0.6, status: 'burn' },
  ),
  'fire-blue': makeProfile(
    'fire-blue', 'fire', 'Azure Flame',
    ['#1ee7ff', '#2f77ff', '#9cecff'], '#efffff', '#38bfff', 'flame',
    { damageType: 'thermal', heat: 900, impulseScale: 1.18, materialMultipliers: { wood: 2.05, organic: 1.9, crystal: 1.18, metal: 0.92 } },
    { hazard: 'flame-field', radius: 2.55, lifetime: 1.8, intensity: 1.05 },
    { gravity: -3.1, drag: 0.48, status: 'burn' },
  ),
  'fire-violet': makeProfile(
    'fire-violet', 'fire', 'Violet Riftflame',
    ['#ff4fd8', '#9d45ff', '#6c2bff'], '#f8e9ff', '#bc67ff', 'flame',
    { damageType: 'thermal', heat: 780, impulseScale: 1.3, materialMultipliers: { wood: 1.95, organic: 1.85, crystal: 1.2, metal: 1.05, stone: 1.05 } },
    { hazard: 'rift-field', radius: 2.8, lifetime: 2.0, intensity: 1.2 },
    { gravity: -2.1, drag: 0.38, status: 'burn' },
  ),
  water: makeProfile(
    'water', 'water', 'Tidal Water',
    ['#1d9bff', '#35d8ff', '#dffbff'], '#eaffff', '#53cfff', 'droplet',
    { damageType: 'water', wetness: 0.9, impulseScale: 0.78, materialMultipliers: { wood: 0.8, metal: 0.72, earth: 1.1 } },
    { hazard: 'steam-field', radius: 2.45, lifetime: 1.8, intensity: 0.72 },
    { gravity: 1.8, drag: 0.9, status: 'soak' },
  ),
  air: makeProfile(
    'air', 'air', 'Aether Wind',
    ['#b9fff2', '#6be2ff', '#ffffff'], '#f3ffff', '#93f4ff', 'ribbon',
    { damageType: 'impact', impulseScale: 1.65, materialMultipliers: { earth: 1.25, stone: 1.1, wood: 1.05 } },
    { hazard: 'wind-field', radius: 2.8, lifetime: 1.25, intensity: 0.65 },
    { gravity: -0.7, drag: 0.2, status: 'gust' },
  ),
  ice: makeProfile(
    'ice', 'ice', 'Frost',
    ['#bfe3ff', '#7fc7ff', '#eaffff'], '#eaffff', '#9fdcff', 'shard',
    { damageType: 'cold', heat: -700, impulseScale: 0.72, materialMultipliers: { water: 1.35, organic: 1.1, metal: 0.78 } },
    { hazard: 'frost-field', radius: 2.3, lifetime: 1.8, intensity: 0.78 },
    { gravity: 1.6, drag: 1.4, status: 'freeze' },
  ),
  lightning: makeProfile(
    'lightning', 'lightning', 'Lightning',
    ['#fff58a', '#ffffff', '#8fe0ff'], '#ffffff', '#fff58a', 'spark',
    { damageType: 'electric', impulseScale: 0.92, materialMultipliers: { metal: 1.55, water: 1.35, earth: 0.7 } },
    { hazard: 'wind-field', radius: 2.1, lifetime: 0.7, intensity: 0.9 },
    { gravity: 0.5, drag: 0.2, status: 'shock' },
  ),
  poison: makeProfile(
    'poison', 'poison', 'Venom',
    ['#9cff2e', '#42d66f', '#eaff8a'], '#f3ffbd', '#6eea66', 'cloud',
    { damageType: 'poison', corrosion: 0.28, impulseScale: 0.25, materialMultipliers: { organic: 1.8, wood: 1.35, stone: 0.55, metal: 0.35 } },
    { hazard: 'poison-cloud', radius: 2.65, lifetime: 2.4, intensity: 0.9 },
    { gravity: -0.35, drag: 0.88, status: 'poison' },
  ),
  acid: makeProfile(
    'acid', 'acid', 'Corrosive Acid',
    ['#d8ff3e', '#77ff00', '#faffc8'], '#fbffd1', '#b8ff38', 'corrosion',
    { damageType: 'acid', corrosion: 0.82, impulseScale: 0.4, materialMultipliers: { metal: 1.9, stone: 1.5, crystal: 0.92, organic: 1.2 } },
    { hazard: 'acid-pool', radius: 2.35, lifetime: 2.7, intensity: 1.0 },
    { gravity: 1.35, drag: 0.7, status: 'corrode' },
  ),
  nature: makeProfile(
    'nature', 'nature', 'Verdant Growth',
    ['#7bdc6c', '#3fae4a', '#c8f27a'], '#e8ffb0', '#7bdc6c', 'leaf',
    { damageType: 'poison', corrosion: 0.18, impulseScale: 0.35, materialMultipliers: { organic: 1.3, earth: 1.2, stone: 0.75 } },
    { hazard: 'poison-cloud', radius: 2.2, lifetime: 1.8, intensity: 0.6 },
    { gravity: 1, drag: 0.8 },
  ),
  void: makeProfile(
    'void', 'void', 'Void',
    ['#2a0840', '#5a1a8a', '#b388ff'], '#d4baff', '#5b2c8a', 'void',
    { damageType: 'implosive', impulseScale: 0.8, materialMultipliers: { crystal: 1.2, stone: 1.05 } },
    { hazard: 'rift-field', radius: 2.6, lifetime: 1.7, intensity: 1.05 },
    { gravity: -1.2, drag: 0.7, status: 'rift' },
  ),
  light: makeProfile(
    'light', 'light', 'Radiance',
    ['#fff6d8', '#ffe9a8', '#ffffff'], '#ffffff', '#fff2c0', 'glow',
    { damageType: 'impact', impulseScale: 0.5, materialMultipliers: {} },
    { hazard: 'none', radius: 0, lifetime: 0, intensity: 0 },
    { gravity: -1.8, drag: 0.5 },
  ),
  earth: makeProfile(
    'earth', 'earth', 'Earth',
    ['#9a7d56', '#c6a878', '#efe1bd'], '#fff1c1', '#c6a878', 'shard',
    { damageType: 'crush', impulseScale: 1.35, materialMultipliers: { stone: 1.25, earth: 1.15, crystal: 1.05, metal: 0.82 } },
    { hazard: 'none', radius: 0, lifetime: 0, intensity: 0 },
    { gravity: 2.4, drag: 1.1 },
  ),
}

export function resolveElementalFx(id: string | undefined | null): ElementalFxProfile {
  return ELYON_ELEMENTAL_FX[id as ElementalFxId] ?? ELYON_ELEMENTAL_FX.physical
}

export function getElementalFx(element: ElementalFxProfile['element'], variant: 'ember' | 'blue' | 'violet' = 'ember'): ElementalFxProfile {
  return element === 'fire' ? resolveElementalFx(`fire-${variant}`) : resolveElementalFx(element)
}

const ATTACK_FX: Record<string, Partial<Record<AttackKind, ElementalFxId>>> = {
  'elyon-protagonist': { light: 'air', heavy: 'water', special: 'fire-violet', throw: 'arcane' },
  'guardian-vulcao': { light: 'fire-ember', heavy: 'fire-blue', special: 'fire-violet', throw: 'fire-ember' },
  'dragao-cristal': { light: 'water', heavy: 'acid', special: 'ice', throw: 'water' },
  'fenix-primordial': { light: 'fire-ember', heavy: 'fire-blue', special: 'fire-violet', throw: 'air' },
  'golem-vivo': { light: 'earth', heavy: 'poison', special: 'acid', throw: 'earth' },
  'tita-montanhas': { light: 'earth', heavy: 'air', special: 'water', throw: 'earth' },
}

export function getAttackElementalFx(fighterId: string, attack: AttackKind | null): ElementalFxProfile {
  const legacy = ATTACK_FX[fighterId]?.[attack ?? 'light']
  if (legacy) return resolveElementalFx(legacy)
  const hero = BATTLE_ARENA_FIGHT_ROSTER.find(candidate => candidate.id === fighterId)
  if (!hero) return resolveElementalFx(attack === 'special' ? 'arcane' : 'physical')
  const signal = attack === 'special'
    ? `${hero.id} ${hero.category} ${hero.special} ${hero.specialName} ${hero.ultimateName} ${hero.abilities.join(' ')}`
    : `${hero.id} ${hero.attack} ${hero.abilities.join(' ')}`
  return elementalFxForSkill(signal)
}

const SKILL_RULES: readonly [RegExp, ElementalFxId][] = [
  [/acid|corros|melt/, 'acid'],
  [/poison|venom|plague|toxic/, 'poison'],
  [/water|tide|wave|flood|rain|splash/, 'water'],
  [/wind|air|gust|cyclone|vortex|updraft/, 'air'],
  [/blue[-_ ]?flame|azure[-_ ]?flame|plasma/, 'fire-blue'],
  [/violet[-_ ]?flame|riftfire|voidfire|purple[-_ ]?flame/, 'fire-violet'],
  [/fire|flame|burn|ember|meteor|lava|inferno|thermal/, 'fire-ember'],
  [/ice|frost|freeze|blizzard|winter|glacial|chill/, 'ice'],
  [/lightning|shock|electr|thunder|storm/, 'lightning'],
  [/void|rift|shadow|dark|corrupt/, 'void'],
  [/light|holy|heal|radiant|prism/, 'light'],
  [/nature|root|thorn|leaf|forest/, 'nature'],
  [/earth|stone|rock|quake|titan|golem/, 'earth'],
]

export function elementalFxForSkill(skillId: string): ElementalFxProfile {
  const normalized = skillId.toLowerCase()
  for (const [pattern, id] of SKILL_RULES) {
    if (pattern.test(normalized)) return resolveElementalFx(id)
  }
  return resolveElementalFx('arcane')
}

export function stageDamageMultiplier(profile: ElementalFxProfile, material: StageMaterial | undefined): number {
  return profile.destruction.materialMultipliers[material ?? 'stone'] ?? 1
}
